<template>
  <b-container fluid>
    <!-- THE FIXED TOP AREA -->
    <b-row class="space-t2 space-b2" id="sticky">
      
      <!-- BACK HOME LINK -->
      <b-col cols="2">
        <router-link to="/">
          <svg id="arrow-left" width="32px" height="32px" viewBox="0 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
              <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                  <g transform="translate(4.000000, 7.000000)">
                      <polygon fill="#000000" transform="translate(12.000000, 9.000000) rotate(-90.000000) translate(-12.000000, -9.000000) " points="11 -3 13 -3 13 21 11 21"></polygon>
                      <polyline stroke="#000000" stroke-width="2" transform="translate(4.500000, 9.000000) rotate(-360.000000) translate(-4.500000, -9.000000) " points="9 4.54747351e-13 0 9 9 18"></polyline>
                  </g>
              </g>
          </svg>
        </router-link>
      </b-col>

      <!-- THE TITLE -->
      <b-col cols="8">
        <h1 v-if="entry.title" class="serif-l">{{entry.title}}</h1>
        <h2 v-if="entry.subtitle" class="serif-l">{{entry.subtitle}}</h2>
      </b-col>
    </b-row>

    <!-- SCROLLING CONTENT AREA -->
    <b-row class="entry-content">
      <!-- THE META INFO -->
      <b-col cols="12" offset="0" sm="10" offset-sm="1" md="8" offset-md="2" class="space-b4 space-t4">
        <p v-if="entry.typ" class="sans-s space-b1">Typ</p>
        <p v-if="entry.typ" class="space-b1">{{entry.typ|capitalize}}</p>
        <p v-if="entry.quarter" class="sans-s space-b1">Quartal</p>
        <p v-if="entry.quarter" class="space-b1">{{entry.quarter|capitalize}}</p>
        <p v-if="entry.subproject" class="sans-s space-b1">Subprojekt</p>
        <p v-if="entry.subproject" class="space-b1"><span v-for="(item, i) in entry.subproject" :key="'subproject-' + i">{{ item|capitalize }}</span></p>
        <p v-if="entry.responsible" class="sans-s space-b1">Eintragsverantwortung</p>
        <p v-if="entry.responsible" class="space-b1">
          <span v-for="(item, i) in entry.responsible" :key="'responsible-' + i" class="responsible">{{ item.fullName }}</span>
        </p>
        <p v-if="entry.dateUpdated" class="sans-s space-b1">Datum</p>
        <p v-if="entry.dateUpdated" class="space-b1">{{entry.dateUpdated|formattedDate}}</p>
        <p v-if="entry.tags" class="sans-s space-b1">Tags</p>
        <p v-if="entry.tags" class="space-b1"><span class="tags" v-for="tag in entry.tags" :key="tag.id">{{ tag.title|capitalize }}</span></p>
      </b-col>

      <!-- TABLE OF CONTENT -->
      <b-col cols="12" offset="0" sm="10" offset-sm="1" md="8" offset-md="2" class="entry-toc serif-m" v-if="toc.length">
        <ul class="toc">
          <p class="mb-3 sans-s">Inhaltsverzeichnis</p>
          <li v-for="(entry, i) in this.toc" :key="'header-'+i" :class="'level-'+entry.level">
            <a href="javascript:;" v-scroll-to="'#'+entry.slug">
              {{entry.title}}
            </a>
          </li>
        </ul>
      </b-col>

      <!-- THE BODY TEXT -->
      <b-col cols="12" offset="0" sm="10" offset-sm="1" md="8" offset-md="2" class="entry-text serif-m" v-html="entry.text"></b-col>

      <!-- THE IMAGES -->
      <b-col v-if="entry.images && entry.images.length" cols="12" offset="0" sm="10" offset-sm="1" md="8" offset-md="2" class="entry-images">
        <h2 class="serif-m">Bilder</h2>
        <a v-for="(image, i) in entry.images" :key="'image-'+i" :href="image.url" target="_blank">
          <img class="image-assets" :src="image.url" :alt="image.title">
        </a>
      </b-col>

      <!-- THE DOCUMENTS -->
      <b-col v-if="entry.documents && entry.documents.length" cols="12" offset="0" sm="10" offset-sm="1" md="8" offset-md="2" class="entry-documents">
        <h2 class="serif-m">Documents</h2>
        <a class="serif-m" v-for="(document, i) in entry.documents" :key="'document-'+i" :href="document.url" target="_blank">
          {{document.title}}
        </a>
      </b-col>

      <!-- THE REFERENCES -->
      <b-col v-if="entry.selectedReferences && entry.selectedReferences.length" cols="12" offset="0" sm="10" offset-sm="1" md="8" offset-md="2" class="entry-text">
        <h2>Referenzen</h2>
        <div class="entry-reference" v-for="(reference, i) in entry.selectedReferences" :key="'reference-'+i" v-html="reference.reference"></div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
const axios = require("axios");

export default {
  name: "Entry",
  metaInfo: () => ({
    link: [{ rel: "favicon", href: "favicon.ico" }],
    // if no subcomponents specify a metaInfo.title, this title will be used
    title: "PIA – Participatory Image Archives",
    // all titles will be injected into this template
    description: "Synergia Project 2021–2025",
  }),
  data() {
    return {
      entry: {},
      toc: []
    };
  },
  created() {
    const slug = this.$route.params.slug;
    axios.post('https://pia-ddn.dhlab.unibas.ch/api', {
      query: `
        query {
          ddb:entries(section: "ddb", slug: "${slug}") {
            title
            author {
              id
              fullName
            }
            ... on ddb_ddb_Entry {
              text
              toc
              quarter
              typ
              dateUpdated
              slug
              uid
              subtitle
              responsible {
                id
                fullName
              }
              collection
              subproject
              tags {
                id
                title
              }
              selectedReferences {
                ... on references_references_Entry {
                  id
                  reference
                }
              }
              images @transform(width: 1000, quality: 70, immediately: true, format: "jpg") {
                title
                url
              }
              documents {
                title
                url
              }
            }
          }
        }
      `
    })
    .then((response) => {
      // console.log(response.data.data);
      this.entry = response.data.data.ddb[0];
    })
    .then(() => {
      if(this.entry.toc) {
        this.populateToc()
      }
    });
  },
  methods: {
    newLineText(el){
      if(el){
        return el.replace(/(?:\r\n|\r|\n)/g, "<br />");
      }
    },
    populateToc(){
      let vm = this;
      let headers = document.querySelector('.entry-content').querySelectorAll('h2, h3, h4');
      headers.forEach(function(el){
        let title = el.innerHTML,
            slug = vm.slugify(title),
            anchor = document.createElement("a");

        anchor.id = slug;
        anchor.classList.add('toc-anchor');

        el.appendChild(anchor);
        vm.toc.push({
          'title': title,
          'slug': slug,
          'level': el.tagName.toLowerCase()
        })
      });
    },
    slugify(text){
      /* eslint-disable no-useless-escape */
      return text.toString().toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
    }
  },
};
</script>

<style lang="scss" scoped>

#sticky {
  position: -webkit-sticky; /* Safari */
  position: sticky;
  top: 0;
  background-color: #F5F5F5;
  z-index: 10;
}

#arrow-left {
  width: 2.4rem;
  cursor: pointer;
}

span {
  padding-right: 0.8rem;
}

.tags {
  border: 1px solid black;
  padding: 0.6rem 0.8rem;
  margin: 0rem 0.8rem 0.8rem 0rem;
  display: inline-block;
}
.responsible:after {
  content: ',';
}
.responsible:last-of-type:after {
  content: none;
}

</style>